import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { debounce, get as _get } from 'lodash'
import { Modal, Select, Spin, Empty } from 'antd'
import {
  backgroundGrey2,
  fadedGrey,
  greenDark,
  greyThemeDark2,
  mobileWidthMax,
  themeColor,
  title,
} from '@edulastic/colors'
import {
  EduButton,
  FlexContainer,
  RadioBtn,
  RadioGrp,
  SelectInputStyled,
  notification,
} from '@edulastic/common'
import { folderSharing, folderTypes, roleuser } from '@edulastic/constants'
import { IconClose } from '@edulastic/icons'
import styled from 'styled-components'
import {
  fetchUsersListAction,
  getFetchingSelector,
  getUsersListSelector,
  updateUsersListAction,
} from '../../../sharedDucks/userDetails'
import {
  shareFolderRequestAction,
  toggleFolderShareModalAction,
} from '../../actions/folder'
import {
  getFoldersSelector,
  getShareFolderIdSelector,
  isFolderSharingSelector,
  isOpenShareModalSelector,
} from '../../selectors/folder'
import { getOrgDataSelector, getUserOrgId } from '../../selectors/user'
import { getFullNameFromAsString } from '../../../../common/utils/helpers'
import { RadioInputWrapper } from '../common/RadioInput'
import {
  buildSharePayload,
  formatUserLabel,
  getUserDisplayName,
  getUserEmail,
  getDistrictViewerForShare,
  getFolderShareSubmitGuard,
  GIVE_ACCESS_MODE,
  hasFolderShareChanges,
  initPendingFromFolder,
  isNewPendingUser,
  isPendingUserChanged,
  shouldShowDistrictInAccessList,
  FOLDER_SHARE_ASSIGNMENT_NOTE,
} from './folderShareHelpers'

const { FOLDER_PERMISSION } = folderSharing
const INDIVIDUAL_SEARCH_TYPE = 'INDIVIDUAL'

const isSameUserId = (left, right) =>
  left != null && right != null && String(left) === String(right)

const NotFoundContent = ({ isSearching }) =>
  isSearching ? (
    <Spin size="small" />
  ) : (
    <Empty
      className="ant-empty-small"
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      style={{ textAlign: 'left', margin: '10px 0' }}
      description="No match found"
    />
  )

const getUserSource = (user) => user?._source || user || {}

const renderUserInfo = (user) => {
  const label = formatUserLabel(user)
  const role = user?.role || user?._source?.role

  return (
    <>
      {label}
      {role ? <UserRole>{` [${roleuser.ROLE_LABEL[role]}]`}</UserRole> : null}
    </>
  )
}

const FolderShareModal = ({
  isOpen,
  shareFolderId,
  folders,
  sharing,
  usersList,
  fetchingUsers,
  currentUserId,
  userOrgData,
  districtId,
  fetchUsers,
  updateShareList,
  shareFolder,
  closeModal,
}) => {
  const folder = folders.find((f) => f._id === shareFolderId) || {}
  const isAssignmentFolder =
    folder.folderType === folderTypes.ASSIGNMENT || folder.folderType === 3
  const originalState = useMemo(() => initPendingFromFolder(folder), [
    folder._id,
    folder.sharedWith,
  ])

  const [pendingUsers, setPendingUsers] = useState(originalState.users)
  const [districtInAccessList, setDistrictInAccessList] = useState(
    originalState.districtViewer
  )
  const [districtRemovedFromAccess, setDistrictRemovedFromAccess] = useState(
    false
  )
  const [giveAccessMode, setGiveAccessMode] = useState(
    originalState.districtViewer
      ? GIVE_ACCESS_MODE.DISTRICT
      : GIVE_ACCESS_MODE.INDIVIDUALS
  )
  const [selectedUsers, setSelectedUsers] = useState([])
  const [givePermission, setGivePermission] = useState(FOLDER_PERMISSION.EDITOR)

  useEffect(() => {
    if (isOpen && shareFolderId) {
      const { users, districtViewer: district } = initPendingFromFolder(folder)
      setPendingUsers(
        users.filter(
          (user) =>
            !isSameUserId(user._id, currentUserId) &&
            !isSameUserId(user._id, folder?.owner?._id)
        )
      )
      setDistrictInAccessList(district)
      setDistrictRemovedFromAccess(false)
      setGiveAccessMode(
        district ? GIVE_ACCESS_MODE.DISTRICT : GIVE_ACCESS_MODE.INDIVIDUALS
      )
      setSelectedUsers([])
      setGivePermission(FOLDER_PERMISSION.EDITOR)
      updateShareList({ data: [] })
    }
  }, [isOpen, shareFolderId, folder._id, currentUserId])

  const searchUser = useCallback(
    debounce((value) => {
      fetchUsers({
        limit: 10,
        page: 1,
        type: INDIVIDUAL_SEARCH_TYPE,
        districtId,
        search: {
          role: ['teacher', 'school-admin', 'district-admin'],
          searchString: value,
          status: 1,
        },
      })
    }, 500),
    [fetchUsers, districtId]
  )

  const handleMultiUserSearch = (value) => {
    if (value.length > 2) {
      searchUser(value)
    } else {
      updateShareList({ data: [] })
    }
  }

  const ownerId = folder?.owner?._id
  const ownerName = getUserDisplayName(folder?.owner) || folder?.owner?.name

  const collaboratorUsers = pendingUsers.filter(
    (user) =>
      !isSameUserId(user._id, currentUserId) && !isSameUserId(user._id, ownerId)
  )

  const onUserSelect = (userId) => {
    if (selectedUsers.find((o) => o._id === userId)) return
    if (collaboratorUsers.some((u) => isSameUserId(u._id, userId))) {
      notification({ messageKey: 'userHasPermission' })
      return
    }
    const user = usersList.find((d) => d._id === userId)
    if (
      !user ||
      isSameUserId(user._id, ownerId) ||
      isSameUserId(user._id, currentUserId)
    ) {
      return
    }
    setSelectedUsers([...selectedUsers, user])
  }

  const onUserDeselect = (userId) => {
    setSelectedUsers(selectedUsers.filter((o) => o._id !== userId))
  }

  const handleRemoveUser = (userId) => {
    setPendingUsers((prev) => prev.filter((u) => u._id !== userId))
  }

  const handlePermissionChange = (userId, permission) => {
    setPendingUsers((prev) =>
      prev.map((u) => (u._id === userId ? { ...u, permission } : u))
    )
  }

  const handleRadioChange = (e) => {
    const mode = e.target.value
    setGiveAccessMode(mode)
    if (mode === GIVE_ACCESS_MODE.DISTRICT) {
      setSelectedUsers([])
      updateShareList({ data: [] })
    }
  }

  const handleShare = () => {
    if (!shareFolderId) return

    const districtViewer = getDistrictViewerForShare({
      giveAccessMode,
      districtInAccessList,
      districtRemovedFromAccess,
    })
    const submitHasChanges = hasFolderShareChanges({
      pendingUsers: collaboratorUsers,
      originalUsers: originalState.users,
      districtViewer,
      originalDistrictViewer: originalState.districtViewer,
      selectedUsers,
    })
    const submitGuard = getFolderShareSubmitGuard({
      hasChanges: submitHasChanges,
      giveAccessMode,
    })
    if (!submitGuard.allow) {
      if (submitGuard.messageKey) {
        notification({ messageKey: submitGuard.messageKey })
      }
      return
    }

    const mergedUsers = [...collaboratorUsers]
    selectedUsers.forEach((user) => {
      if (mergedUsers.some((u) => isSameUserId(u._id, user._id))) return
      if (
        isSameUserId(user._id, ownerId) ||
        isSameUserId(user._id, currentUserId)
      ) {
        return
      }
      const source = getUserSource(user)
      mergedUsers.push({
        _id: user._id,
        name: getFullNameFromAsString(source).trim() || source.email,
        email: source.email,
        permission: givePermission,
      })
    })
    const payload = buildSharePayload(mergedUsers, districtViewer)
    shareFolder({
      folderId: shareFolderId,
      folderType: folder.folderType,
      ...payload,
    })
  }

  const handleClose = () => {
    closeModal({ isOpen: false })
  }

  const { districts = [{}] } = userOrgData || {}
  const { districtName = '' } =
    districts.find((d) => d.districtId === districtId) || {}
  const districtLabel = districtName
    ? `Anyone in ${districtName}`
    : 'Anyone in district'

  const filteredUserList = (usersList || []).filter((user) => {
    if (
      isSameUserId(user._id, currentUserId) ||
      isSameUserId(user._id, ownerId)
    ) {
      return false
    }
    if (
      collaboratorUsers.some((people) => isSameUserId(people._id, user._id))
    ) {
      return false
    }
    return true
  })
  const multiSelectUserList = [
    ...filteredUserList,
    ...selectedUsers.filter(
      (user) => !filteredUserList.some((u) => u._id === user._id)
    ),
  ]

  const pendingDistrictViewer = getDistrictViewerForShare({
    giveAccessMode,
    districtInAccessList,
    districtRemovedFromAccess,
  })

  const hasChanges = hasFolderShareChanges({
    pendingUsers: collaboratorUsers,
    originalUsers: originalState.users,
    districtViewer: pendingDistrictViewer,
    originalDistrictViewer: originalState.districtViewer,
    selectedUsers,
  })

  const showDistrictInAccessList = shouldShowDistrictInAccessList({
    districtInAccessList,
    districtRemovedFromAccess,
  })

  return (
    <SharingModal
      visible={isOpen}
      onCancel={handleClose}
      footer={null}
      closable={false}
      centered
      width={640}
      destroyOnClose
    >
      <ModalContainer>
        <Header>
          <Title>Share Folder</Title>
          <CloseButton
            type="button"
            onClick={handleClose}
            aria-label="Close share folder modal"
          >
            <IconClose />
          </CloseButton>
        </Header>

        <ShareListTitle>WHO HAS ACCESS</ShareListTitle>
        <AccessList data-cy="folderShareList">
          {ownerId && (
            <AccessRow key="folder-owner">
              <UserInfo>
                {formatUserLabel(folder?.owner) || ownerName || 'Owner'}
              </UserInfo>
              <AccessLevelLabel>Owner</AccessLevelLabel>
              <RemoveButtonSpacer aria-hidden="true" />
            </AccessRow>
          )}
          {collaboratorUsers.map((user) => {
            const isChanged =
              isNewPendingUser(user, originalState.users) ||
              isPendingUserChanged(user, originalState.users)
            return (
              <AccessRow key={user._id}>
                <UserInfo>{renderUserInfo(user)}</UserInfo>
                <PermissionSelect
                  value={user.permission}
                  $pending={isChanged}
                  onChange={(val) => handlePermissionChange(user._id, val)}
                  getPopupContainer={(trigger) => trigger.parentNode}
                  data-cy="folder-share-permission"
                >
                  <Select.Option value={FOLDER_PERMISSION.VIEWER}>
                    Viewer
                  </Select.Option>
                  <Select.Option value={FOLDER_PERMISSION.EDITOR}>
                    Editor
                  </Select.Option>
                </PermissionSelect>
                <RemoveButton
                  type="button"
                  onClick={() => handleRemoveUser(user._id)}
                  aria-label={`Remove ${
                    getUserDisplayName(user) || getUserEmail(user)
                  }`}
                >
                  <IconClose width={12} height={12} />
                </RemoveButton>
              </AccessRow>
            )
          })}
          {showDistrictInAccessList && (
            <AccessRow key="district-viewer">
              <UserInfo>{districtLabel}</UserInfo>
              <AccessLevelLabel>Viewer</AccessLevelLabel>
              <RemoveButton
                type="button"
                onClick={() => {
                  setDistrictRemovedFromAccess(true)
                  if (giveAccessMode === GIVE_ACCESS_MODE.DISTRICT) {
                    setGiveAccessMode(GIVE_ACCESS_MODE.INDIVIDUALS)
                  }
                }}
                aria-label="Remove district access"
              >
                <IconClose width={12} height={12} />
              </RemoveButton>
            </AccessRow>
          )}
        </AccessList>

        <PeopleBlock>
          <ShareListTitle>GIVE ACCESS TO</ShareListTitle>
          <RadioBtnWrapper>
            <RadioGrp value={giveAccessMode} onChange={handleRadioChange}>
              <RadioBtn value={GIVE_ACCESS_MODE.DISTRICT}>DISTRICT</RadioBtn>
              <RadioBtn value={GIVE_ACCESS_MODE.INDIVIDUALS}>
                INDIVIDUALS
              </RadioBtn>
            </RadioGrp>
          </RadioBtnWrapper>
          {giveAccessMode === GIVE_ACCESS_MODE.INDIVIDUALS ? (
            <FlexContainer
              style={{ marginTop: 5, position: 'relative' }}
              justifyContent="flex-start"
              alignItems="flex-start"
            >
              <UserMultiSelectWrapper>
                <UserMultiSelectStyled
                  mode="multiple"
                  bg="white"
                  height="40px"
                  optionLabelProp="chipLabel"
                  placeholder="Enter names or email addresses"
                  value={selectedUsers.map((x) => x._id)}
                  onSearch={handleMultiUserSearch}
                  loading={fetchingUsers}
                  onSelect={onUserSelect}
                  onDeselect={onUserDeselect}
                  dropdownClassName="custom-antd-select"
                  filterOption={false}
                  notFoundContent={
                    <NotFoundContent isSearching={fetchingUsers} />
                  }
                  getPopupContainer={(triggerNode) => triggerNode.parentNode}
                  data-cy="folder-share-user-search"
                >
                  {multiSelectUserList.map((user) => {
                    const source = getUserSource(user)
                    const name = `${source.firstName || ''} ${
                      source.lastName || ''
                    }`.trim()
                    const email = source.email || ''
                    const role = source.role
                    return (
                      <Select.Option
                        key={user._id}
                        value={user._id}
                        chipLabel={name || email}
                        title={`${name} (${email})${
                          role ? ` [${roleuser.ROLE_LABEL[role]}]` : ''
                        }`}
                      >
                        <span>
                          {name}
                          {email ? `(${email})` : ''}{' '}
                          {role ? (
                            <b>{`[${roleuser.ROLE_LABEL[role]}]`}</b>
                          ) : null}
                        </span>
                      </Select.Option>
                    )
                  })}
                </UserMultiSelectStyled>
              </UserMultiSelectWrapper>
              <PermissionSelectWrapper>
                <GiveAccessSelect
                  onChange={setGivePermission}
                  data-cy="folder-share-give-permission"
                  value={givePermission}
                  getPopupContainer={(triggerNode) => triggerNode.parentNode}
                >
                  <Select.Option value={FOLDER_PERMISSION.VIEWER}>
                    Viewer
                  </Select.Option>
                  <Select.Option value={FOLDER_PERMISSION.EDITOR}>
                    Editor
                  </Select.Option>
                </GiveAccessSelect>
              </PermissionSelectWrapper>
            </FlexContainer>
          ) : (
            <ShareMessageWrapper data-cy="folderShareDistrictMessage">
              {`${districtLabel} as Viewer`}
            </ShareMessageWrapper>
          )}
        </PeopleBlock>

        <FooterActions>
          <FooterNotes>
            {isAssignmentFolder && (
              <FooterNote data-cy="folderShareAssignmentNote">
                {FOLDER_SHARE_ASSIGNMENT_NOTE}
              </FooterNote>
            )}
            {hasChanges && (
              <FooterNote $highlight>
                Click Share to apply the updated sharing access
              </FooterNote>
            )}
          </FooterNotes>
          <FooterButtons>
            <EduButton isGhost variant="create" onClick={handleClose}>
              CANCEL
            </EduButton>
            <EduButton
              color="primary"
              variant="create"
              onClick={handleShare}
              disabled={sharing}
              loading={sharing}
              data-cy="shareFolderSubmit"
            >
              SHARE
            </EduButton>
          </FooterButtons>
        </FooterActions>
      </ModalContainer>
    </SharingModal>
  )
}

FolderShareModal.propTypes = {
  isOpen: PropTypes.bool,
  shareFolderId: PropTypes.string,
  folders: PropTypes.array,
  sharing: PropTypes.bool,
  usersList: PropTypes.array,
  fetchingUsers: PropTypes.bool,
  currentUserId: PropTypes.string,
  userOrgData: PropTypes.object,
  districtId: PropTypes.string,
  fetchUsers: PropTypes.func,
  updateShareList: PropTypes.func,
  shareFolder: PropTypes.func,
  closeModal: PropTypes.func,
}

FolderShareModal.defaultProps = {
  isOpen: false,
  shareFolderId: null,
  folders: [],
  sharing: false,
  usersList: [],
  fetchingUsers: false,
  currentUserId: '',
  userOrgData: {},
  districtId: '',
  fetchUsers: () => null,
  updateShareList: () => null,
  shareFolder: () => null,
  closeModal: () => null,
}

export default connect(
  (state) => ({
    isOpen: isOpenShareModalSelector(state),
    shareFolderId: getShareFolderIdSelector(state),
    folders: getFoldersSelector(state),
    sharing: isFolderSharingSelector(state),
    usersList: getUsersListSelector(state),
    fetchingUsers: getFetchingSelector(state),
    currentUserId: _get(state, 'user.user._id', ''),
    userOrgData: getOrgDataSelector(state),
    districtId: getUserOrgId(state),
  }),
  {
    fetchUsers: fetchUsersListAction,
    updateShareList: updateUsersListAction,
    shareFolder: shareFolderRequestAction,
    closeModal: toggleFolderShareModalAction,
  }
)(FolderShareModal)

const SharingModal = styled(Modal)`
  .ant-modal-content {
    margin: 15px 0px;
  }
  .ant-modal-body {
    padding: 30px;
  }
`

const ModalContainer = styled.div`
  .anticon-down {
    svg {
      fill: ${themeColor};
    }
  }
`

const Header = styled(FlexContainer)`
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${fadedGrey};
`

const Title = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${title};
`

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  svg {
    fill: ${title};
  }
`

const ShareListTitle = styled.div`
  margin-top: 10px;
  text-transform: uppercase;
  font-size: 13px;
  font-weight: 600;
  color: ${greyThemeDark2};
`

const AccessList = styled.div`
  margin-top: 10px;
`

const AccessRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  margin-bottom: 8px;
  border-radius: 4px;
  background: ${backgroundGrey2};
`

const UserInfo = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  font-size: 13px;
  color: ${title};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const PermissionSelect = styled(SelectInputStyled)`
  flex: 0 0 100px;
  width: 100px;

  &.ant-select {
    width: 100px !important;
    min-width: 100px;
  }

  ${({ $pending }) =>
    $pending &&
    `
    .ant-select-selection-selected-value {
      color: #8A6700;
    }
  `}
`

const AccessLevelLabel = styled.div`
  flex: 0 0 100px;
  width: 100px;
  font-size: 13px;
  font-weight: 600;
  color: ${title};
  text-align: left;
`

const RemoveButton = styled.button`
  flex: 0 0 auto;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  svg {
    fill: ${greenDark};
  }
`

const RemoveButtonSpacer = styled.div`
  flex: 0 0 20px;
  width: 20px;
  height: 20px;
`

const PeopleBlock = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;

  .ant-radio {
    margin-right: 5px;
  }
`

const RadioBtnWrapper = styled(RadioInputWrapper)`
  font-weight: 600;
  margin: 10px 0px;
`

const ShareMessageWrapper = styled.div`
  height: 40px;
  line-height: 40px;
  flex: 1 1 0;
  min-width: 0;
  font-size: 13px;
`

const UserMultiSelectWrapper = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  margin-right: 10px;
  align-self: flex-start;
`

const PermissionSelectWrapper = styled.div`
  flex: 0 0 140px;
  width: 140px;
  max-width: 140px;
  align-self: flex-start;

  .ant-select {
    width: 100%;
    height: 40px;

    .ant-select-selection {
      box-sizing: border-box;
      height: 40px;
      min-height: 40px;
    }

    .ant-select-selection--single .ant-select-selection__rendered {
      line-height: 38px;
    }
  }

  @media (max-width: ${mobileWidthMax}) {
    width: 100%;
    max-width: 100%;
    margin-top: 5px;
  }
`

const GiveAccessSelect = styled(SelectInputStyled)`
  &.ant-select {
    @media (max-width: ${mobileWidthMax}) {
      width: 100%;
      margin-top: 5px;
    }
  }
`

const UserMultiSelectStyled = styled(SelectInputStyled)`
  &.ant-select {
    width: 100%;
    vertical-align: top;
    min-height: 40px;
    max-height: 110px;
    height: auto;

    &:not(:has(.ant-select-selection__choice)) {
      height: 40px;
    }

    .ant-select-selection.ant-select-selection--multiple {
      align-items: center;
      box-sizing: border-box;
      height: auto !important;
      min-height: 40px;
      max-height: 110px;
      overflow-y: auto;
    }

    &:not(:has(.ant-select-selection__choice))
      .ant-select-selection.ant-select-selection--multiple {
      height: 40px !important;
    }

    .ant-select-selection__choice__content {
      text-transform: none;
    }
  }
`

const UserRole = styled.b`
  font-weight: 600;
`

const FooterNote = styled.p`
  flex: 1;
  min-width: 0;
  font-size: 12px;
  font-weight: 400;
  color: ${({ $highlight }) => ($highlight ? '#8A6700' : title)};
  margin: 0;
  padding-right: 12px;
`

const FooterNotes = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const FooterActions = styled(FlexContainer)`
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  gap: 12px;
`

const FooterButtons = styled(FlexContainer)`
  flex-shrink: 0;
  gap: 12px;
  margin-left: auto;
`
